/**
 * @file     closingCosts.ts
 * @purpose  The one set of BC buyer closing-cost defaults and the itemised
 *           low-to-high estimate built on them. Statutory lines (transfer tax)
 *           are exact; third-party lines are typical ranges and are labelled
 *           that way. Pure and client-safe.
 */
import { minimumDownPayment } from '@/core/finance/downPayment';
import { mortgageInsurance } from '@/core/finance/mortgageInsurance';
import { estimatePropertyTransferTax } from '@/core/tax/transferTax';

/** Date the third-party ranges were last reviewed. */
export const CLOSING_COSTS_VERIFIED_ON = '2026-10-06';

/**
 * Typical third-party ranges in BC, shown as estimates and never as quotes.
 * `typical` is the single figure the cost-of-buying city pages use for the
 * four lines they show.
 */
export const CLOSING_COST_RANGES = {
  legal: { low: 1_200, typical: 1_800, high: 2_500 },
  titleInsurance: { low: 200, typical: 250, high: 400 },
  appraisal: { low: 300, typical: 400, high: 600 },
  inspection: { low: 400, typical: 600, high: 800 },
  strataDocuments: { low: 35, high: 300 },
  adjustments: { low: 300, high: 2_000 },
  moving: { low: 500, high: 3_000 },
} as const;

/**
 * BC charges no PST on a mortgage default insurance premium. CMHC: "Some
 * provinces (currently Ontario, Quebec and Saskatchewan) apply provincial
 * sales tax to the mortgage loan insurance premium."
 * Source: https://www.cmhc-schl.gc.ca/en/finance-and-investing/mortgage-loan-insurance/the-resource/mortgage-loan-insurance-and-premiums
 */
export const PROVINCES_WITH_SALES_TAX_ON_PREMIUM = ['Ontario', 'Quebec', 'Saskatchewan'] as const;

const range = ({ low, high }: { low: number; high: number }) => ({ low, high });

export type ClosingCostKey =
  | 'transferTax'
  | 'legal'
  | 'titleInsurance'
  | 'appraisal'
  | 'inspection'
  | 'strataDocuments'
  | 'adjustments'
  | 'moving';

export interface ClosingCostLine {
  key: ClosingCostKey;
  low: number;
  high: number;
  /** Exact statutory amount rather than a typical range. */
  exact: boolean;
}

export interface ClosingCostInputs {
  price: number;
  /** Down payment as a share of price, e.g. 0.1 for 10%. */
  downPaymentShare: number;
  firstTimeBuyer?: boolean;
  strata?: boolean;
}

export interface ClosingCostEstimate {
  price: number;
  downPayment: number;
  minimumDown: number;
  /** True when the down payment is below the legal minimum for the price. */
  belowMinimumDown: boolean;
  lines: ClosingCostLine[];
  totalLow: number;
  totalHigh: number;
  /** Down payment plus closing costs. */
  cashToCloseLow: number;
  cashToCloseHigh: number;
  transferTax: number;
  transferTaxWithoutExemption: number;
  /** Default insurance premium, added to the mortgage rather than paid in cash. */
  insurancePremium: number;
  insurancePremiumRate: number;
}

export function estimateClosingCosts(input: ClosingCostInputs): ClosingCostEstimate {
  const price = Number.isFinite(input.price) && input.price > 0 ? input.price : 0;
  const share = Math.min(Math.max(input.downPaymentShare, 0), 1);
  const downPayment = Math.round(price * share);
  const minimumDown = minimumDownPayment(price);

  const ptt = estimatePropertyTransferTax({ price, firstTimeBuyer: input.firstTimeBuyer });
  const insurance = mortgageInsurance(price, downPayment);

  const lines: ClosingCostLine[] = [
    { key: 'transferTax', low: ptt.taxAfterExemption, high: ptt.taxAfterExemption, exact: true },
    { key: 'legal', ...range(CLOSING_COST_RANGES.legal), exact: false },
    { key: 'titleInsurance', ...range(CLOSING_COST_RANGES.titleInsurance), exact: false },
    { key: 'appraisal', ...range(CLOSING_COST_RANGES.appraisal), exact: false },
    { key: 'inspection', ...range(CLOSING_COST_RANGES.inspection), exact: false },
  ];
  if (input.strata) {
    lines.push({ key: 'strataDocuments', ...range(CLOSING_COST_RANGES.strataDocuments), exact: false });
  }
  lines.push({ key: 'adjustments', ...range(CLOSING_COST_RANGES.adjustments), exact: false });
  lines.push({ key: 'moving', ...range(CLOSING_COST_RANGES.moving), exact: false });

  const totalLow = lines.reduce((sum, l) => sum + l.low, 0);
  const totalHigh = lines.reduce((sum, l) => sum + l.high, 0);

  return {
    price,
    downPayment,
    minimumDown,
    belowMinimumDown: price > 0 && downPayment < minimumDown,
    lines,
    totalLow,
    totalHigh,
    cashToCloseLow: downPayment + totalLow,
    cashToCloseHigh: downPayment + totalHigh,
    transferTax: ptt.taxAfterExemption,
    transferTaxWithoutExemption: ptt.generalTax,
    insurancePremium: insurance.premium,
    insurancePremiumRate: insurance.premiumRate,
  };
}
